import { z } from "zod"

const key = z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/)
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const timestamp = z.string().datetime({ offset: true })
const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`)
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  }, "Invalid calendar date")
export const operationalKindSchema = z.enum(["uptime", "scenario", "incident-project", "deploy-project"])

/** Explicit human-approved ownership, never inferred from product names or URLs. */
export const reportingOperationalBindingSchema = z
  .object({
    key,
    productKey: key,
    kind: operationalKindSchema,
    externalId: z.string().trim().min(1).max(200),
    environment: z.enum(["prod", "dev", "local", "production", "staging", "preview", "development", "unknown"]),
    includedFrom: timestamp,
    intervalSeconds: z.number().int().min(1).max(604800).nullable(),
  })
  .strict()
  .superRefine((binding, ctx) => {
    if (
      (binding.kind === "uptime" || binding.kind === "scenario") &&
      !z.string().uuid().safeParse(binding.externalId).success
    )
      ctx.addIssue({ code: "custom", message: "Monitor bindings use exact registration UUIDs" })
    if (binding.kind === "scenario" && !["prod", "dev", "local"].includes(binding.environment))
      ctx.addIssue({ code: "custom", message: "Scenario environment must match its registration" })
    if (binding.kind === "incident-project" && binding.environment !== "unknown")
      ctx.addIssue({
        code: "custom",
        message: "Incident projects have no authoritative environment column; preserve unknown scope",
      })
    if (
      binding.kind === "deploy-project" &&
      !["production", "staging", "preview", "development", "unknown"].includes(binding.environment)
    )
      ctx.addIssue({ code: "custom", message: "Use the provider deployment environment" })
    if (!["uptime", "scenario"].includes(binding.kind) && binding.intervalSeconds !== null)
      ctx.addIssue({ code: "custom", message: "Only checks have a cadence" })
  })

export const operationalPeriodSchema = z
  .object({ startInclusive: timestamp, endExclusive: timestamp })
  .strict()
  .refine((period) => Date.parse(period.startInclusive) < Date.parse(period.endExclusive), "Invalid operational period")

export const operationalCheckCountsSchema = z
  .object({
    recorded: count,
    passed: count,
    failed: count,
    expected: count.nullable(),
    unobserved: count.nullable(),
    latencySumMs: z.number().finite().nonnegative(),
    latencySamples: count,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.recorded !== value.passed + value.failed || value.latencySamples > value.recorded)
      ctx.addIssue({ code: "custom", message: "Invalid check accounting" })
    if (
      (value.expected === null) !== (value.unobserved === null) ||
      (value.expected !== null && value.unobserved !== Math.max(0, value.expected - value.recorded))
    )
      ctx.addIssue({ code: "custom", message: "Invalid missing-check accounting" })
  })

export const operationalCheckSchema = z
  .object({
    bindingKey: key,
    kind: z.enum(["uptime", "scenario"]),
    name: z.string().max(200),
    environment: z.string().max(30),
    enabledAtCollection: z.boolean(),
    intervalSeconds: z.number().positive().nullable(),
    scheduleBasis: z.literal("configured-cadence-not-historical-SLO"),
    current: operationalCheckCountsSchema,
    prior: operationalCheckCountsSchema,
    daily: z.array(z.object({ date, ...operationalCheckCountsSchema.innerType().shape }).strict()).max(14),
    lastRecordedAt: timestamp.nullable(),
    lastSuccess: z.boolean().nullable(),
    staleAtCollection: z.boolean().nullable(),
    alertCount: count.nullable(),
    historyMethod: z.enum(["raw", "raw-plus-transactionally-moved-hourly-rollups"]),
  })
  .strict()

export const operationalIncidentCountsSchema = z
  .object({
    overlapping: count,
    opened: count,
    resolved: count,
    ongoingAtEnd: count,
  })
  .strict()
  .refine(
    (value) =>
      value.opened <= value.overlapping &&
      value.resolved <= value.overlapping &&
      value.ongoingAtEnd <= value.overlapping,
    "Invalid incident accounting",
  )

export const operationalDeployCountsSchema = z
  .object({
    completed: count,
    succeeded: count,
    failed: count,
    canceled: count,
    running: count,
    evaluated: count,
    healthy: count,
    degraded: count,
    healthFailed: count,
    analyzing: count,
    notEvaluated: count,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      value.completed !== value.succeeded + value.failed ||
      value.evaluated !== value.healthy + value.degraded + value.healthFailed ||
      value.evaluated + value.analyzing + value.notEvaluated !== value.succeeded
    )
      ctx.addIssue({ code: "custom", message: "Invalid provider/evaluation accounting" })
  })

const moduleState = {
  status: z.enum(["available", "partial", "missing"]),
  reasons: z
    .array(
      z.enum([
        "BINDING_MISSING",
        "SOURCE_UNAVAILABLE",
        "NO_OBSERVATION",
        "CADENCE_UNVERIFIED",
        "BOUNDARY_COVERAGE",
        "ROW_CAP",
        "OWNERSHIP_MISMATCH",
      ]),
    )
    .max(7),
}

export const reportingOperationsSchema = z
  .object({
    schemaVersion: z.literal(1),
    collectedAt: timestamp,
    timezone: z.literal("America/Los_Angeles"),
    current: operationalPeriodSchema,
    prior: operationalPeriodSchema,
    bindings: z.array(reportingOperationalBindingSchema).max(200),
    products: z
      .array(
        z
          .object({
            productKey: key,
            checks: z.object({ ...moduleState, rows: z.array(operationalCheckSchema).max(100) }).strict(),
            incidents: z
              .object({
                ...moduleState,
                currentUnified: operationalIncidentCountsSchema.nullable(),
                priorUnified: operationalIncidentCountsSchema.nullable(),
                currentLegacy: operationalIncidentCountsSchema.nullable(),
                priorLegacy: operationalIncidentCountsSchema.nullable(),
                environment: z.literal("unknown"),
              })
              .strict(),
            deployments: z
              .object({
                ...moduleState,
                rows: z
                  .array(
                    z
                      .object({
                        bindingKey: key,
                        project: z.string().max(200),
                        environment: z.string().max(30),
                        current: operationalDeployCountsSchema,
                        prior: operationalDeployCountsSchema,
                      })
                      .strict(),
                  )
                  .max(100),
                periodBasis: z.literal("completed-at-for-terminal-started-at-for-running"),
              })
              .strict(),
          })
          .strict(),
      )
      .min(1)
      .max(20),
  })
  .strict()
  .superRefine((value, ctx) => {
    const products = new Set(value.products.map((product) => product.productKey))
    const bindings = new Map(value.bindings.map((binding) => [binding.key, binding]))
    if (
      products.size !== value.products.length ||
      bindings.size !== value.bindings.length ||
      value.bindings.some((binding) => !products.has(binding.productKey))
    )
      ctx.addIssue({ code: "custom", message: "Invalid operational roster/bindings" })
    if (
      value.prior.endExclusive !== value.current.startInclusive ||
      Date.parse(value.current.endExclusive) > Date.parse(value.collectedAt)
    )
      ctx.addIssue({ code: "custom", message: "Operational windows must be adjacent and complete" })
    const seen = new Set<string>()
    for (const product of value.products) {
      for (const row of [...product.checks.rows, ...product.deployments.rows]) {
        if (seen.has(row.bindingKey) || bindings.get(row.bindingKey)?.productKey !== product.productKey)
          ctx.addIssue({ code: "custom", message: "Operational row is outside its configured product" })
        seen.add(row.bindingKey)
      }
      for (const row of product.checks.rows) {
        const binding = bindings.get(row.bindingKey)
        if (binding?.kind !== row.kind || binding?.environment !== row.environment)
          ctx.addIssue({ code: "custom", message: "Check kind/environment differs from binding" })
        const days = new Set<string>()
        for (const day of row.daily) {
          const { date: dayDate, ...counts } = day
          if (
            days.has(dayDate) ||
            dayDate < value.prior.startInclusive.slice(0, 10) ||
            dayDate >= value.current.endExclusive.slice(0, 10) ||
            !operationalCheckCountsSchema.safeParse(counts).success
          )
            ctx.addIssue({ code: "custom", message: "Invalid daily check accounting/window" })
          days.add(dayDate)
        }
        if (days.size !== 14)
          ctx.addIssue({
            code: "custom",
            message: "Check history must disclose all fourteen days, including no-observation days",
          })
        for (const period of ["current", "prior"] as const) {
          const daily = row.daily.filter(
            (day) =>
              day.date >= value[period].startInclusive.slice(0, 10) &&
              day.date < value[period].endExclusive.slice(0, 10),
          )
          for (const metric of ["recorded", "passed", "failed", "latencySamples", "latencySumMs"] as const) {
            const total = daily.reduce((sum, day) => sum + day[metric], 0)
            const tolerance = metric === "latencySumMs" ? Math.max(1, total) * 1e-10 : 0
            if (Math.abs(total - row[period][metric]) > tolerance)
              ctx.addIssue({ code: "custom", message: "Daily check observations do not reconcile to their period" })
          }
        }
      }
      for (const row of product.deployments.rows) {
        const binding = bindings.get(row.bindingKey)
        if (
          binding?.kind !== "deploy-project" ||
          binding?.externalId !== row.project ||
          binding?.environment !== row.environment
        )
          ctx.addIssue({ code: "custom", message: "Deployment row differs from binding" })
      }
      for (const module of [product.checks, product.incidents, product.deployments]) {
        if (
          (module.status === "available" && module.reasons.length) ||
          (module.status !== "available" && !module.reasons.length)
        )
          ctx.addIssue({ code: "custom", message: "Module status must explain incomplete evidence" })
      }
    }
  })

export type ReportingOperationalBinding = z.infer<typeof reportingOperationalBindingSchema>
export type ReportingOperations = z.infer<typeof reportingOperationsSchema>
export type OperationalCheck = z.infer<typeof operationalCheckSchema>
export type OperationalCheckCounts = z.infer<typeof operationalCheckCountsSchema>
export type OperationalIncidentCounts = z.infer<typeof operationalIncidentCountsSchema>
export type OperationalDeployCounts = z.infer<typeof operationalDeployCountsSchema>
